import { section } from './helpers.ts';
import type { Exercise } from '../types.ts';

export const reading: Exercise[] = [
  {
    id: 'reading-longitude',
    category: 'reading',
    title: 'Longitude',
    topic: 'Navigation · Time and longitude',
    difficulty: 'Moderate',
    article: {
      title: 'Longitude',
      url: 'https://en.wikipedia.org/wiki/Longitude',
      scope: {
        wordCount: 2384,
        sections: [
          {
            title: 'Introduction',
            url: 'https://en.wikipedia.org/wiki/Longitude',
            wordCount: 304,
            anchors: [''],
          },
          {
            title: 'History',
            url: 'https://en.wikipedia.org/wiki/Longitude#History',
            wordCount: 1057,
            anchors: ['History', 'The_Chronometer'],
          },
          {
            title: 'Determination',
            url: 'https://en.wikipedia.org/wiki/Longitude#Determination',
            wordCount: 705,
            anchors: ['Determination'],
          },
          {
            title: 'Length of a degree of longitude',
            url: 'https://en.wikipedia.org/wiki/Longitude#Length_of_a_degree_of_longitude',
            wordCount: 318,
            anchors: ['Length_of_a_degree_of_longitude'],
          },
        ],
      },
      reviewedOn: '2026-09-27',
      preview:
        'Finding an east–west position at sea was once a major navigational challenge. This article connects the geometry of longitude with timekeeping and the development of methods for measuring it.',
    },
    reading: {
      title: 'Longitude',
      authorship:
        'Local explanations written for these questions. The assigned reading is the linked Wikipedia article.',
      sources: [{ title: 'Wikipedia · Longitude', url: 'https://en.wikipedia.org/wiki/Longitude' }],
      sections: [
        section(
          'rotation',
          'Longitude and distance',
          'Longitude measures an east–west angle, not a distance in kilometres. A meridian joins points with the same longitude from pole to pole. Two meridians that are far apart at the equator meet at the poles. The east–west distance between them therefore depends on latitude.\n\nOn a spherical Earth, the radius of a circle of latitude is the equatorial radius multiplied by the cosine of latitude. At 60° latitude that factor is one half. A fixed change in longitude spans half as many kilometres along that parallel as along the equator. The angle has not changed; the circle on which we measure its arc has.',
        ),
        section(
          'two-clocks',
          'Comparing local time with reference time',
          'To infer longitude from time, compare two readings taken at the same instant: the local solar time where you are and the time at a known reference meridian. Earth turns through 360° in about 24 hours relative to the Sun, giving 15° per hour of time difference. A reference clock must retain the reference meridian’s time; resetting it to local time would discard the measurement you need.\n\nSuppose it is noon at your location while the reference meridian is already at 3 p.m. Noon reached the reference meridian three hours earlier, so you are west of it. Multiplying three hours by 15° per hour gives 45° west. This calculation treats the two readings as comparable solar times; a real navigator must account for the difference between apparent solar time and a clock’s mean time.',
        ),
        section(
          'accuracy',
          'Converting clock error into position error',
          'A longitude measurement made with a reference clock depends on the clock’s accuracy. At 15° per hour, each minute of timing error produces 15/60 = 0.25° of longitude error. Two minutes therefore produce an angular error of 0.5°. This is the magnitude of the error, regardless of whether the clock is fast or slow.\n\nThe calculation concerns time minutes and angular degrees. They are different units: one minute on a clock is not one minute of angular measurement. To turn the angular error into kilometres, you would also need the latitude, because meridians are closer together near the poles.',
        ),
        section(
          'solar-civil',
          'Civil time and solar time',
          'Civil time is the time a region has agreed to display on its clocks. Solar time is tied to the Sun’s apparent position at a particular longitude. Towns can share a civil time convention while their local solar noons occur at different instants.\n\nConsequently, equal phone-clock readings do not establish equal longitude. A longitude calculation needs a local astronomical observation compared with a known reference time, rather than a comparison of two clocks that have both been set to the same regional convention.',
        ),
        section(
          'principle',
          'Why timekeeping at sea was difficult',
          'The clock method of finding longitude was understood before a suitable sea-going clock existed. A clock that keeps good time on land may fail when a ship rolls and changes temperature. Pendulum clocks were especially sensitive to the ship’s motion. An incorrect reference time then became an incorrect inferred longitude.\n\nMarine chronometers addressed that practical problem: preserve a sufficiently accurate reference time throughout a voyage. They did not remove the need to find local time astronomically. The navigator still compared a local observation with the transported time; the improved instrument made that comparison reliable enough to use at sea.',
        ),
      ],
    },
    questions: [
      {
        id: 'longitude-1',
        prompt:
          'At your local solar noon, a reference clock shows 3 p.m. at its meridian. Treat both readings as solar time, ignoring the equation-of-time correction. Where are you relative to the reference meridian?',
        options: [
          {
            id: '0',
            text: '45° east',
          },
          {
            id: '1',
            text: '20° west',
          },
          {
            id: '2',
            text: '45° west',
          },
          {
            id: '3',
            text: '3° west',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation:
          'The reference meridian reached noon three hours earlier. Your location is west by 3 × 15° = 45°.',
        sectionIds: ['two-clocks'],
        evidence:
          'Comparison of local and reference time; 15 degrees per hour. The prompt specifies comparable solar times.',
        sourceSection: {
          title: 'Determination',
          url: 'https://en.wikipedia.org/wiki/Longitude#Determination',
        },
      },
      {
        id: 'longitude-2',
        prompt: 'Why must the ship’s reference clock remain unadjusted to local solar time?',
        options: [
          {
            id: '0',
            text: 'Resetting it would erase the difference used to infer longitude.',
          },
          {
            id: '1',
            text: 'It must preserve elapsed voyage time, which directly gives longitude.',
          },
          {
            id: '2',
            text: 'Only latitude changes local solar time, so adjustments would be irrelevant.',
          },
          {
            id: '3',
            text: 'Local solar time is determined by civil time-zone boundaries.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation:
          'The method compares local solar time with the retained reference time. Resetting the reference to local time removes the signal.',
        sectionIds: ['two-clocks'],
        evidence:
          'Transport of chronometers retains time at a known starting meridian for comparison with local time.',
        sourceSection: {
          title: 'Determination',
          url: 'https://en.wikipedia.org/wiki/Longitude#Determination',
        },
      },
      {
        id: 'longitude-3',
        prompt:
          'Using the longitude–time relationship of 15° per hour, how large an angular longitude error would a reference clock running two minutes fast introduce?',
        options: [
          {
            id: '0',
            text: '2°',
          },
          {
            id: '1',
            text: '30°',
          },
          {
            id: '2',
            text: '0.125°',
          },
          {
            id: '3',
            text: '0.5°',
          },
        ],
        answer: {
          kind: 'choice',
          value: '3',
        },
        explanation:
          'Four minutes correspond to one degree, so two minutes correspond to half a degree.',
        sectionIds: ['accuracy'],
        evidence:
          'The stated 15 degrees per hour gives 0.25 degree per time minute; two minutes gives 0.5 degree.',
        sourceSection: {
          title: 'Determination',
          url: 'https://en.wikipedia.org/wiki/Longitude#Determination',
        },
      },
      {
        id: 'longitude-chronometer',
        prompt:
          'Why was an accurate pendulum clock on land insufficient for finding longitude aboard a ship?',
        options: [
          {
            id: '0',
            text: 'It measured latitude rather than time.',
          },
          {
            id: '1',
            text: 'Its timekeeping was disrupted by the ship’s motion.',
          },
          {
            id: '2',
            text: 'The time–longitude relationship changes at sea.',
          },
          {
            id: '3',
            text: 'Local astronomical observations became unnecessary.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation:
          'Ship motion disrupted pendulum timekeeping, so the transported reference time was unreliable. Marine chronometers were developed to keep time under sea-going conditions.',
        sectionIds: ['principle'],
        evidence:
          'The marine environment made accurate observations difficult, and pendulum clocks did not work well with an ocean swell.',
        sourceSection: {
          title: 'History',
          url: 'https://en.wikipedia.org/wiki/Longitude#History',
        },
      },
      {
        id: 'longitude-5',
        prompt: 'Why does a 15° longitude difference not specify a fixed distance in kilometres?',
        options: [
          {
            id: '0',
            text: 'The angular rate of Earth’s rotation changes with latitude.',
          },
          {
            id: '1',
            text: 'Longitude is a measure of distance along the equator only.',
          },
          {
            id: '2',
            text: 'Circles of latitude shrink toward the poles.',
          },
          {
            id: '3',
            text: 'Each time zone defines a different length for the same degree.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation:
          'The same longitude angle spans a smaller distance along a circle of latitude nearer the poles.',
        sectionIds: ['rotation'],
        evidence:
          'The article gives a cosine-of-latitude dependence and explains the shrinking circles of latitude.',
        sourceSection: {
          title: 'Length of a degree of longitude',
          url: 'https://en.wikipedia.org/wiki/Longitude#Length_of_a_degree_of_longitude',
        },
      },
    ],
    retiredQuestions: [
      {
        id: 'longitude-4',
        prompt:
          'Two towns show the same civil time on their phones. What can you conclude from that alone?',
        options: [
          {
            id: '0',
            text: 'They have the same longitude.',
          },
          {
            id: '1',
            text: 'They use the same displayed time convention at that moment.',
          },
          {
            id: '2',
            text: 'Their solar noons coincide.',
          },
          {
            id: '3',
            text: 'They are equally far from Greenwich in miles.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation:
          'Civil time groups places by convention and does not uniquely determine their longitude or solar noon.',
        sectionIds: ['solar-civil'],
        evidence:
          'Civil time groups places by convention and does not uniquely determine their longitude or solar noon.',
      },
    ],
  },
  {
    id: 'reading-plates',
    category: 'reading',
    title: 'Plate tectonics',
    topic: 'Earth science · Plate tectonics',
    difficulty: 'Moderate',
    article: {
      title: 'Plate tectonics',
      url: 'https://en.wikipedia.org/wiki/Plate_tectonics',
      scope: {
        wordCount: 1959,
        sections: [
          {
            title: 'Introduction',
            url: 'https://en.wikipedia.org/wiki/Plate_tectonics',
            wordCount: 446,
            anchors: [''],
          },
          {
            title: 'Key principles',
            url: 'https://en.wikipedia.org/wiki/Plate_tectonics#Key_principles',
            wordCount: 612,
            anchors: ['Key_principles'],
          },
          {
            title: 'Types of plate boundaries',
            url: 'https://en.wikipedia.org/wiki/Plate_tectonics#Types_of_plate_boundaries',
            wordCount: 302,
            anchors: ['Types_of_plate_boundaries'],
          },
          {
            title: 'Magnetic striping',
            url: 'https://en.wikipedia.org/wiki/Plate_tectonics#Magnetic_striping',
            wordCount: 599,
            anchors: ['Magnetic_striping'],
          },
        ],
      },
      reviewedOn: '2026-09-27',
      preview:
        'Earth’s surface changes over geological time. This article explains the plate model, the evidence that established it, and the processes associated with different kinds of plate boundary.',
    },
    reading: {
      title: 'Plate tectonics',
      authorship:
        'Local explanations written for these questions. The assigned reading is the linked Wikipedia article.',
      sources: [
        {
          title: 'Wikipedia · Plate tectonics',
          url: 'https://en.wikipedia.org/wiki/Plate_tectonics',
        },
      ],
      sections: [
        section(
          'layers',
          'What moves when a plate moves',
          'A tectonic plate is a piece of lithosphere: the crust together with the rigid uppermost mantle. A coastline marks the edge of exposed land, so it need not mark the edge of a plate. One plate can carry both a continent and adjoining ocean floor.\n\nBelow the lithosphere is hotter mantle that can deform slowly under sustained stress. Calling it able to flow does not mean it is a worldwide pool of liquid magma. Rock that behaves as a solid in a brief experiment can change shape over geological time. This distinction explains how a rigid plate can move above material that is largely solid.',
        ),
        section(
          'divergence',
          'Why ocean-floor ages increase away from a ridge',
          'At a spreading ridge, plates move apart and magma produces new oceanic crust. The newly formed strip moves away as later strips form beside it. Distance from the ridge is therefore related to time since formation: in a simple spreading system, the youngest rock is near the ridge and older rock lies farther away.\n\nImagine marking the new crust at a ridge today. After a period of spreading, the mark would lie to one side of the ridge, with newer crust between it and the ridge. The pattern follows the production and motion of crust, rather than distance from a coastline. Changes in spreading rate can change the spacing without reversing that age sequence.',
        ),
        section(
          'convergence',
          'How oceanic lithosphere is recycled',
          'Seafloor spreading creates oceanic crust at divergent boundaries. At some convergent boundaries, one plate bends beneath another and descends into the mantle. This process is subduction. Formation and removal can occur at the same time in different places.\n\nNew seafloor therefore does not require a steadily expanding Earth. The surface material added at ridges is balanced on a global scale by material returned at subduction zones. A coastline alone is not evidence of subduction: the relevant feature is a convergent plate boundary where a plate actually descends.',
        ),
        section(
          'transform',
          'Recognizing a transform boundary',
          'At a transform boundary, neighbouring plates move sideways relative to one another. This motion does not systematically open a gap for new lithosphere or carry one plate beneath the other. Those would be divergent and convergent motions respectively.\n\nSideways movement can still produce earthquakes. Parts of a fault may resist sliding while the surrounding rocks continue to deform. When a locked section slips, the stored deformation is released. Frequent earthquakes therefore do not by themselves imply either seafloor creation or subduction; the relative motion and other evidence determine the type of boundary.',
        ),
        section(
          'evidence',
          'Connecting plate motion with evidence',
          'Plate tectonics connects observations made at different locations. Young oceanic crust forms near ridges; magnetic patterns occur in roughly matching bands on either side; and older oceanic lithosphere can descend at subduction zones. These observations describe the creation, movement, and recycling of the same outer layer.\n\nNo individual ocean basin has to preserve every strip of crust forever. Once a strip enters a subduction zone, its former position is no longer part of the exposed ocean floor. This explains why continuous production can coexist with a roughly constant planetary surface area and why the surviving seafloor is an incomplete record of Earth’s history.',
        ),
      ],
    },
    questions: [
      {
        id: 'plates-1',
        prompt: 'Which description best matches a tectonic plate?',
        options: [
          {
            id: '0',
            text: 'Continental crust only',
          },
          {
            id: '1',
            text: 'Any region enclosed by a coastline',
          },
          {
            id: '2',
            text: 'Liquid mantle beneath one continent',
          },
          {
            id: '3',
            text: 'Crust and rigid uppermost mantle moving together',
          },
        ],
        answer: {
          kind: 'choice',
          value: '3',
        },
        explanation:
          'The lithosphere includes crust and rigid uppermost mantle; plates can include oceanic and continental regions.',
        sectionIds: ['layers'],
        evidence:
          'Lithosphere comprises crust and rigid uppermost mantle; plates may contain continental and oceanic crust.',
        sourceSection: {
          title: 'Key principles',
          url: 'https://en.wikipedia.org/wiki/Plate_tectonics#Key_principles',
        },
      },
      {
        id: 'plates-2',
        prompt: 'In a simple ocean-spreading system, what rock-age pattern should you expect?',
        options: [
          {
            id: '0',
            text: 'Older at the ridge, younger away',
          },
          {
            id: '1',
            text: 'Younger at the ridge, older away',
          },
          {
            id: '2',
            text: 'The same age across the basin',
          },
          {
            id: '3',
            text: 'Age follows distance from the nearest coast only',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation:
          'New oceanic crust forms at the ridge, then moves outward as additional crust is produced.',
        sectionIds: ['divergence'],
        evidence:
          'The evidence list explicitly places very young rocks at the ridge and progressively older rocks farther away.',
        sourceSection: {
          title: 'Magnetic striping',
          url: 'https://en.wikipedia.org/wiki/Plate_tectonics#Magnetic_striping',
        },
      },
      {
        id: 'plates-3',
        prompt:
          'A boundary produces frequent earthquakes but no new strip of crust and no systematic consumption of crust. Which idealized motion fits?',
        options: [
          {
            id: '0',
            text: 'Sideways sliding',
          },
          {
            id: '1',
            text: 'Seafloor spreading',
          },
          {
            id: '2',
            text: 'Oceanic subduction',
          },
          {
            id: '3',
            text: 'Continental collision with crustal thickening',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation:
          'Transform boundaries accommodate sideways relative motion without systematic creation or consumption of lithosphere.',
        sectionIds: ['transform'],
        evidence:
          'Transform boundaries describe sideways relative movement, in contrast with divergence and subduction.',
        sourceSection: {
          title: 'Types of plate boundaries',
          url: 'https://en.wikipedia.org/wiki/Plate_tectonics#Types_of_plate_boundaries',
        },
      },
      {
        id: 'plates-4',
        prompt: 'Why is “plates float on a global ocean of liquid rock” misleading?',
        options: [
          {
            id: '0',
            text: 'The mantle deforms only during the few seconds of an earthquake.',
          },
          {
            id: '1',
            text: 'The underlying mantle is perfectly rigid on all timescales.',
          },
          {
            id: '2',
            text: 'Much of the underlying mantle is solid but deforms over long times.',
          },
          {
            id: '3',
            text: 'Only the crust moves; the rigid upper mantle remains stationary.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation: 'Solid mantle can deform under sustained stress on geological timescales.',
        sectionIds: ['layers'],
        evidence:
          'The article distinguishes rigid lithosphere from more ductile asthenosphere and describes creeping solid mantle.',
        sourceSection: {
          title: 'Key principles',
          url: 'https://en.wikipedia.org/wiki/Plate_tectonics#Key_principles',
        },
      },
      {
        id: 'plates-5',
        prompt:
          'Why can new ocean floor form continuously without requiring Earth to keep expanding?',
        options: [
          {
            id: '0',
            text: 'Ridge crust becomes thinner by exactly the amount of all new production.',
          },
          {
            id: '1',
            text: 'New ocean floor is formed only when another ridge closes.',
          },
          {
            id: '2',
            text: 'Continents consume all adjacent seafloor along their coastlines.',
          },
          {
            id: '3',
            text: 'Lithosphere is also returned to the mantle at subduction zones.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '3',
        },
        explanation:
          'Production at ridges and recycling at subduction zones are parts of the same system.',
        sectionIds: ['convergence', 'evidence'],
        evidence:
          'New oceanic crust from spreading balances surface returned to the mantle by subduction.',
        sourceSection: {
          title: 'Introduction',
          url: 'https://en.wikipedia.org/wiki/Plate_tectonics',
        },
      },
    ],
  },
  {
    id: 'reading-maps',
    category: 'reading',
    title: 'Map projection',
    topic: 'Cartography · Models and tradeoffs',
    difficulty: 'Moderate',
    article: {
      title: 'Map projection',
      url: 'https://en.wikipedia.org/wiki/Map_projection',
      scope: {
        wordCount: 1721,
        sections: [
          {
            title: 'Introduction',
            url: 'https://en.wikipedia.org/wiki/Map_projection',
            wordCount: 390,
            anchors: [''],
          },
          {
            title: 'Metric properties of maps',
            url: 'https://en.wikipedia.org/wiki/Map_projection#Metric_properties_of_maps',
            wordCount: 592,
            anchors: ['Metric_properties_of_maps', 'Distortion', 'Other_distortion_metrics'],
          },
          {
            title: 'Normal cylindrical',
            url: 'https://en.wikipedia.org/wiki/Map_projection#Normal_cylindrical',
            wordCount: 373,
            anchors: ['Normal_cylindrical'],
          },
          {
            title: 'Conformal',
            url: 'https://en.wikipedia.org/wiki/Map_projection#Conformal',
            wordCount: 125,
            anchors: ['Conformal'],
          },
          {
            title: 'Equal-area',
            url: 'https://en.wikipedia.org/wiki/Map_projection#Equal-area',
            wordCount: 80,
            anchors: ['Equal-area'],
          },
          {
            title: 'Equidistant',
            url: 'https://en.wikipedia.org/wiki/Map_projection#Equidistant',
            wordCount: 161,
            anchors: ['Equidistant'],
          },
        ],
      },
      reviewedOn: '2026-09-27',
      preview:
        'A flat map represents a curved world. This article introduces the choices involved in that transformation, the properties different projections preserve, and the trade-offs that shape a useful map.',
    },
    reading: {
      title: 'Map projection',
      authorship:
        'Local explanations written for these questions. The assigned reading is the linked Wikipedia article.',
      sources: [
        {
          title: 'Wikipedia · Map projection',
          url: 'https://en.wikipedia.org/wiki/Map_projection',
        },
      ],
      sections: [
        section(
          'flatten',
          'Why a flat map must distort',
          'A map projection assigns positions on a curved surface to positions on a flat sheet. A globe and a plane have different geometry. Covering a whole spherical surface with a flat map therefore changes some distances, areas, or angles, even if the original survey measurements are exact.\n\nThe unavoidable distortion does not make every map equally suitable. It makes the property being preserved matter. A map can be designed to retain relative areas or local angles, for example, while allowing other measurements to change. More accurate geographic data improve where features are placed; they cannot remove the geometric limitation of flattening a sphere.',
        ),
        section(
          'area',
          'Comparing areas on a map',
          'An equal-area projection applies one common factor to surface areas. If one region occupies twice the area of another on Earth, it occupies twice the area on the map. That is the property needed when comparing the sizes of countries.\n\nPreserving area does not also preserve every shape or route. A region can become stretched in one direction and compressed in another while keeping its area. Conversely, a map with familiar-looking local shapes can substantially exaggerate the area of some regions. Choose the area property when the quantity being read from the map is area.',
        ),
        section(
          'angles',
          'Local angles and long routes',
          'A conformal projection preserves angles at a point. On a Mercator map, the scale at a particular point is the same in every direction, but that scale varies from place to place. Preserving a local angle consequently does not promise correct continent sizes or distances between arbitrary cities.\n\nMercator also draws a route of constant compass bearing, called a rhumb line, as a straight line. Keeping the same bearing is a different requirement from minimizing distance over a sphere. The shortest surface route follows a great circle; in general its compass bearing changes. A straight Mercator route can therefore be convenient to steer without being the shortest route.',
        ),
        section(
          'distance',
          'Which distances an equidistant map preserves',
          'An equidistant projection preserves a specified family of distances. For example, a projection centred on a station can preserve distances from that station to every destination. Each such measurement has the station as one endpoint.\n\nA route between two other cities has neither endpoint at the station. It falls outside the stated guarantee. Preserving station-to-A and station-to-B distances does not determine the correct map distance from A to B. Always keep the endpoints in the claim: “from the centre” is a condition on the measurement, not a claim that the scale is correct everywhere.',
        ),
        section(
          'purpose',
          'Matching a projection to a measurement',
          'Suppose two maps show the same countries. On one, the countries’ area ratios are correct; on the other, small angles and local shapes are correct. The right choice depends on whether the task is comparing land area or interpreting local direction.\n\nA map can also deliberately compromise, spreading several kinds of distortion without eliminating any one of them. Its balanced appearance is useful for some purposes, but it is not a mathematical guarantee about a measurement. To use a projection quantitatively, identify what it preserves and whether that property covers the comparison you intend to make.',
        ),
      ],
    },
    retiredQuestions: [
      {
        id: 'maps-2',
        prompt: 'A projection preserves angles locally. What does that guarantee?',
        options: [
          {
            id: '0',
            text: 'Every continent has its true area.',
          },
          {
            id: '1',
            text: 'All straight lines are shortest routes.',
          },
          {
            id: '2',
            text: 'Every pair of cities has a correct separation.',
          },
          {
            id: '3',
            text: 'Angles are preserved near a point, even if scale varies elsewhere.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '3',
        },
        explanation:
          'Conformality is a local angle property, not a global guarantee about area or distance.',
        sectionIds: ['angles'],
        evidence:
          'Conformal projections preserve local angles; the article explicitly distinguishes constant local scale from scale varying across the map.',
        sourceSection: {
          title: 'Conformal',
          url: 'https://en.wikipedia.org/wiki/Map_projection#Conformal',
        },
      },
    ],
    questions: [
      {
        id: 'maps-1',
        prompt: 'Which projection property best serves a comparison of countries’ land areas?',
        options: [
          {
            id: '0',
            text: 'Conformal',
          },
          {
            id: '1',
            text: 'Equal-area',
          },
          {
            id: '2',
            text: 'Straight rhumb lines',
          },
          {
            id: '3',
            text: 'Correct distance from one center',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation:
          'Equal-area projections preserve relative areas, which is the quantity being compared.',
        sectionIds: ['area'],
        evidence: 'Equal-area maps preserve area measure, usually with shape distortion.',
        sourceSection: {
          title: 'Equal-area',
          url: 'https://en.wikipedia.org/wiki/Map_projection#Equal-area',
        },
      },
      {
        id: 'maps-local-scale',
        prompt:
          'On a conformal map, a very short east–west segment at a particular location has a scale of 2 map centimetres per kilometre. At that same point, what scale should a very short north–south segment have?',
        options: [
          { id: '0', text: '1 cm per kilometre, because the directions are perpendicular.' },
          { id: '1', text: '4 cm per kilometre, because both dimensions are scaled.' },
          { id: '2', text: 'It cannot be inferred without knowing the map’s total area.' },
          {
            id: '3',
            text: '2 cm per kilometre, because local scale is the same in every direction.',
          },
        ],
        answer: { kind: 'choice', value: '3' },
        explanation:
          'Conformality preserves angles by using the same local scale in every direction at a point. The scale may still differ at another location; this does not guarantee equal areas or correct long-distance measurements.',
        sectionIds: ['angles'],
        evidence:
          'The assigned Conformal section defines equal scale in every direction at a point; the question applies that local property to perpendicular short segments.',
        sourceSection: {
          title: 'Conformal',
          url: 'https://en.wikipedia.org/wiki/Map_projection#Conformal',
        },
      },
      {
        id: 'maps-3',
        prompt: 'Why can a straight route on a Mercator map be longer than another surface route?',
        options: [
          {
            id: '0',
            text: 'A constant-bearing path need not be a great-circle route.',
          },
          {
            id: '1',
            text: 'Conformality preserves area rather than route length.',
          },
          {
            id: '2',
            text: 'Great-circle routes always require a constant compass bearing.',
          },
          {
            id: '3',
            text: 'A printed map’s scale is constant everywhere, but Earth’s size varies.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation:
          'Mercator makes constant-bearing rhumb lines straight; the shortest spherical route generally follows a great circle.',
        sectionIds: ['angles'],
        evidence:
          'Mercator represents constant-bearing rhumbs as straight lines; compare the great-circle discussion under Azimuthal projections.',
        sourceSection: {
          title: 'Normal cylindrical',
          url: 'https://en.wikipedia.org/wiki/Map_projection#Normal_cylindrical',
        },
      },
      {
        id: 'maps-4',
        prompt:
          'A map preserves distance from its central station to every city. Which additional claim is unjustified?',
        options: [
          {
            id: '0',
            text: 'The station-to-city distances are useful.',
          },
          {
            id: '1',
            text: 'The projection has a stated condition.',
          },
          {
            id: '2',
            text: 'Distances between every pair of cities must also be correct.',
          },
          {
            id: '3',
            text: 'The center has a special role.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation:
          'Preserving distances from a designated center does not preserve all pairwise distances.',
        sectionIds: ['distance'],
        evidence:
          'The guarantee covers distances from one or two special points, not all pairs of points.',
        sourceSection: {
          title: 'Equidistant',
          url: 'https://en.wikipedia.org/wiki/Map_projection#Equidistant',
        },
      },
      {
        id: 'maps-5',
        prompt: 'Why is there no single flat world map that preserves all distances everywhere?',
        options: [
          {
            id: '0',
            text: 'Survey measurements are still too imprecise for an exact projection.',
          },
          {
            id: '1',
            text: 'A sphere cannot be flattened into a plane without distortion.',
          },
          {
            id: '2',
            text: 'Mapmakers must preserve area on every projection.',
          },
          {
            id: '3',
            text: 'Political boundaries force the projection to stretch selected countries.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation:
          'The incompatibility comes from the curved surface and flat plane, not data quality or drawing precision.',
        sectionIds: ['flatten'],
        evidence:
          'A spherical surface cannot be mapped to a plane without distortion; the limitation is geometrical.',
        sourceSection: {
          title: 'Distortion',
          url: 'https://en.wikipedia.org/wiki/Map_projection#Distortion',
        },
      },
    ],
  },
  {
    id: 'reading-locks',
    category: 'reading',
    title: 'Lock (water navigation)',
    topic: 'Engineering · Navigation locks',
    difficulty: 'Moderate',
    article: {
      title: 'Lock (water navigation)',
      url: 'https://en.wikipedia.org/wiki/Lock_(water_navigation)',
      scope: {
        wordCount: 1866,
        sections: [
          {
            title: 'Introduction',
            url: 'https://en.wikipedia.org/wiki/Lock_(water_navigation)',
            wordCount: 118,
            anchors: [''],
          },
          {
            title: 'Basic operation',
            url: 'https://en.wikipedia.org/wiki/Lock_(water_navigation)#Basic_operation',
            wordCount: 313,
            anchors: ['Basic_operation'],
          },
          {
            title: 'Doubled, paired or twinned locks',
            url: 'https://en.wikipedia.org/wiki/Lock_(water_navigation)#Doubled,_paired_or_twinned_locks',
            wordCount: 383,
            anchors: ['Doubled,_paired_or_twinned_locks'],
          },
          {
            title: 'Lock flights',
            url: 'https://en.wikipedia.org/wiki/Lock_(water_navigation)#Lock_flights',
            wordCount: 1052,
            anchors: ['Lock_flights', 'Staircase_locks', 'Stop_locks'],
          },
        ],
      },
      reviewedOn: '2026-09-27',
      preview:
        'Canals and rivers do not stay at one elevation. This article describes the structures that let boats move between water levels, how they operate, and how different arrangements manage water use.',
    },
    reading: {
      title: 'Lock (water navigation)',
      authorship:
        'Local explanations written for these questions. The assigned reading is the linked Wikipedia article.',
      sources: [
        {
          title: 'Wikipedia · Lock (water navigation)',
          url: 'https://en.wikipedia.org/wiki/Lock_(water_navigation)',
        },
      ],
      sections: [
        section(
          'problem',
          'What a lock changes',
          'A conventional navigation lock has a fixed chamber between stretches of water at different heights. Gates isolate the chamber so that its water level can be changed. A boat inside remains afloat and follows the rising or falling surface.\n\nThe chamber itself stays in place. This distinguishes the lock from a boat lift or an inclined plane, where the boat or its water-filled container is moved. Both arrangements can overcome an elevation difference, but the component that moves is different. In a conventional lock, changing the amount of water in a stationary chamber does the lifting.',
        ),
        section(
          'up',
          'Moving from the lower level to the upper level',
          'To move a boat uphill through a conventional lock, the chamber initially matches the lower stretch of water. The boat enters through the lower gate. That gate then closes, isolating the chamber from the lower water level. Water admitted from upstream raises the chamber surface and the floating boat.\n\nLeaving the lower gate open would give the incoming water a route straight downstream. Closing it is therefore necessary before filling. Once the chamber reaches the upper level, the upper gate opens and the boat leaves. The sequence matters because the gates control which stretch of water the chamber is connected to at each stage.',
        ),
        section(
          'down',
          'Water flow in a lock',
          'A conventional lock fills with water from the upstream side and empties toward the downstream side. The water changes height under gravity. The boat rises when the chamber fills because it floats on the rising surface, rather than because a pump or crane lifts the hull.\n\nGoing downhill reverses the sequence: admit the boat at the upper level, close the upper gate, drain the chamber downstream, then open the lower gate for departure. Some systems use pumps to replenish their water supply. That is a separate task from the gravity-fed filling and emptying of the chamber described here.',
        ),
        section(
          'float',
          'Calculating the volume for a level change',
          'Assume a vertical-sided chamber with horizontal area 300 square metres. Its level rises 5 metres, the boat stays afloat with unchanged displacement, and leakage is ignored. A layer one metre deep contains 300 cubic metres, so five such layers require 1,500 cubic metres.\n\nThe boat displaces the same volume at both levels, so its displacement cancels when the starting water volume is subtracted from the final water volume.',
        ),
        section(
          'system',
          'Why the upper water supply is used up',
          'In normal lock operation, water admitted from the upper reach is later discharged toward the lower reach. Gravity supplies the immediate flow, but it does not send that water back uphill. Repeated operation can therefore reduce the upper water supply unless it is replenished.\n\nThis is why water-saving arrangements matter even when the chamber can fill without a pump. A smaller suitable chamber uses less water for a level change. A storage basin or neighbouring chamber can retain some water that would otherwise be discharged, reducing how much new water must come from upstream.',
        ),
        section(
          'twinned',
          'Reusing water between paired locks',
          'Two adjacent locks can sometimes be connected so water leaving one helps fill the other. During the useful part of the transfer, one chamber has water at a higher level than the other. The shared water supplies part of the second chamber’s rise instead of all being sent downstream.\n\nThis arrangement saves water by reuse; it does not reduce the elevation difference boats must cross. Merely building two locks side by side does not guarantee that they can exchange water. The article distinguishes paired capacity from an arrangement with connecting passages and coordinated operation that permits this transfer.',
        ),
        section(
          'pressure',
          'Why gate levels are matched',
          'Water pressure increases with depth. If a lock gate has high water on one side and low water on the other, the forces acting on the gate are unequal. Matching the water levels reduces that imbalance before the gate is opened.\n\nWater still presses on both faces of the gate after the levels match. Equalization reduces the difference between the forces; it does not remove pressure itself. Nor does it change a floating boat’s weight or eliminate the volume the boat displaces.',
        ),
      ],
    },
    questions: [
      {
        id: 'locks-1',
        prompt:
          'A boat has entered a chamber from the lower pool. What should happen before the chamber is filled?',
        options: [
          {
            id: '0',
            text: 'Open the upper gate fully.',
          },
          {
            id: '1',
            text: 'Drain the upper pool to the lower level.',
          },
          {
            id: '2',
            text: 'Close the lower gate.',
          },
          {
            id: '3',
            text: 'Lift the boat clear of the water.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation:
          'Closing the lower gate isolates the chamber so incoming water can raise its level.',
        sectionIds: ['up'],
        evidence: 'The upstream sequence closes the lower gates before filling the chamber.',
        sourceSection: {
          title: 'Basic operation',
          url: 'https://en.wikipedia.org/wiki/Lock_(water_navigation)#Basic_operation',
        },
      },
      {
        id: 'locks-fixed-chamber',
        prompt:
          'What distinguishes a conventional navigation lock from a boat lift with a moving water-filled container?',
        options: [
          {
            id: '0',
            text: 'The lock changes water level in a fixed chamber.',
          },
          {
            id: '1',
            text: 'The lock moves its chamber between elevations.',
          },
          {
            id: '2',
            text: 'The lock changes the boat’s weight.',
          },
          {
            id: '3',
            text: 'The lock requires equal water levels along the whole canal.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation:
          'The lock chamber stays fixed while its water level changes. A boat lift instead moves the boat or a container holding it.',
        sectionIds: ['problem'],
        evidence:
          'The defining feature is a fixed chamber with variable water level, contrasted with a moving caisson or boat lift.',
        sourceSection: {
          title: 'Introduction',
          url: 'https://en.wikipedia.org/wiki/Lock_(water_navigation)',
        },
      },
      {
        id: 'locks-3',
        prompt: 'How can the simple lock raise a boat without a pump?',
        options: [
          {
            id: '0',
            text: 'Pressure beneath the boat increases its displaced volume until it rises.',
          },
          {
            id: '1',
            text: 'The upper pool’s gravity-fed water raises the chamber surface.',
          },
          {
            id: '2',
            text: 'The boat becomes lighter as deeper water increases buoyancy.',
          },
          {
            id: '3',
            text: 'Opening the lower gate creates suction toward the upper pool.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation:
          'Water from the upper pool supplies the level change through gravity-driven flow.',
        sectionIds: ['down'],
        evidence:
          'The upstream and downstream diagrams show filling from upstream and emptying downstream.',
        sourceSection: {
          title: 'Basic operation',
          url: 'https://en.wikipedia.org/wiki/Lock_(water_navigation)#Basic_operation',
        },
      },
      {
        id: 'locks-twinned-water',
        prompt:
          'How can appropriately connected twinned locks reduce their use of water from the upper canal?',
        options: [
          {
            id: '0',
            text: 'They remove the height difference between the upper and lower canal.',
          },
          {
            id: '1',
            text: 'They make boats displace no water while entering.',
          },
          {
            id: '2',
            text: 'They drain both chambers completely before either boat enters.',
          },
          {
            id: '3',
            text: 'Water from an emptying chamber helps fill the other.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '3',
        },
        explanation:
          'Reusing water between chambers supplies part of the filling volume that would otherwise need to come from upstream.',
        sectionIds: ['twinned'],
        evidence:
          'Connected chambers can exchange water; water from an emptying chamber helps fill the other.',
        sourceSection: {
          title: 'Doubled, paired or twinned locks',
          url: 'https://en.wikipedia.org/wiki/Lock_(water_navigation)#Doubled,_paired_or_twinned_locks',
        },
      },
      {
        id: 'locks-5',
        prompt:
          'Why can water supply constrain lock operation even when the lock needs no pump for filling?',
        options: [
          {
            id: '0',
            text: 'Each cycle transfers water from the higher level toward the lower level.',
          },
          {
            id: '1',
            text: 'Each rising boat needs an additional permanent volume equal to its displacement.',
          },
          {
            id: '2',
            text: 'Chamber water must evaporate before a downward cycle begins.',
          },
          {
            id: '3',
            text: 'Equalizing levels requires the entire upper pool to drain into the chamber.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation:
          'Repeated cycles spend water from the upper system, which may need replenishment.',
        sectionIds: ['system'],
        evidence:
          'The discussion of recycling water to the top of a flight supports the downstream water-use cycle shown in Basic operation.',
        sourceSection: {
          title: 'Lock flights',
          url: 'https://en.wikipedia.org/wiki/Lock_(water_navigation)#Lock_flights',
        },
      },
    ],
    retiredQuestions: [
      {
        id: 'locks-2',
        prompt: 'Why equalize levels before opening a gate?',
        options: [
          {
            id: '0',
            text: 'It reduces the pressure difference across the gate.',
          },
          {
            id: '1',
            text: 'It removes the water pressure on both sides of the gate.',
          },
          {
            id: '2',
            text: 'It reduces the boat’s displacement to zero before departure.',
          },
          {
            id: '3',
            text: 'It makes the boat’s draft equal to the full chamber depth.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation:
          'Equal levels reduce the unbalanced pressure forces; they do not eliminate pressure or gravity.',
        sectionIds: ['pressure'],
        evidence:
          'Equal levels reduce the unbalanced pressure forces; they do not eliminate pressure or gravity.',
      },
      {
        id: 'locks-4',
        prompt:
          'A lock has a vertical-sided chamber with a horizontal area of 300 m². Its water level rises by 5 m. Ignoring leakage and changes in boat displacement, approximately how much water enters the chamber?',
        options: [
          {
            id: '0',
            text: '60 m³',
          },
          {
            id: '1',
            text: '305 m³',
          },
          {
            id: '2',
            text: 'The boat’s displacement only',
          },
          {
            id: '3',
            text: '1,500 m³',
          },
        ],
        answer: {
          kind: 'choice',
          value: '3',
        },
        explanation:
          'Area times height gives 300 × 5 = 1,500 cubic metres. Constant boat displacement cancels in the difference.',
        sectionIds: ['float'],
        evidence:
          'Area times height gives 300 × 5 = 1,500 cubic metres. Constant boat displacement cancels in the difference.',
      },
    ],
  },
  {
    id: 'reading-tides',
    category: 'reading',
    title: 'Tide',
    topic: 'Ocean science · Tides',
    difficulty: 'Moderate',
    article: {
      title: 'Tide',
      url: 'https://en.wikipedia.org/wiki/Tide',
      scope: {
        wordCount: 2311,
        sections: [
          {
            title: 'Introduction',
            url: 'https://en.wikipedia.org/wiki/Tide',
            wordCount: 233,
            anchors: [''],
          },
          {
            title: 'Characteristics',
            url: 'https://en.wikipedia.org/wiki/Tide#Characteristics',
            wordCount: 635,
            anchors: ['Characteristics', 'Reference_levels', 'Range_variation:_springs_and_neaps'],
          },
          {
            title: 'Tidal constituents',
            url: 'https://en.wikipedia.org/wiki/Tide#Tidal_constituents',
            wordCount: 1123,
            anchors: [
              'Tidal_constituents',
              'Principal_lunar_semi-diurnal_constituent',
              'Lunar_distance',
              'Other_constituents',
              'Phase_and_amplitude',
            ],
          },
          {
            title: 'Tide Table',
            url: 'https://en.wikipedia.org/wiki/Tide#Tide_Table',
            wordCount: 125,
            anchors: ['Tide_Table'],
          },
          {
            title: 'Example calculation',
            url: 'https://en.wikipedia.org/wiki/Tide#Example_calculation',
            wordCount: 195,
            anchors: ['Example_calculation'],
          },
        ],
      },
      reviewedOn: '2026-09-27',
      preview:
        'Coastal water levels follow patterns that vary by place and time. This article covers the causes of tides, the vocabulary used to describe them, and how observations become predictions.',
    },
    reading: {
      title: 'Tide',
      authorship:
        'Local explanations written for these questions. The assigned reading is the linked Wikipedia article.',
      sources: [{ title: 'Wikipedia · Tide', url: 'https://en.wikipedia.org/wiki/Tide' }],
      sections: [
        section(
          'difference',
          'Why a difference in gravity matters',
          'The Moon attracts the entire Earth, including its rock and water. Its attraction is stronger on the side nearer the Moon and weaker on the farther side. Tidal effects arise from these differences across Earth, measured relative to the motion of Earth as a whole.\n\nOn the near side, water is pulled toward the Moon more strongly than Earth’s centre is. On the far side, it is pulled toward the Moon less strongly than the centre is. Both comparisons tend to stretch the Earth–ocean system along the Earth–Moon direction. No gravitational repulsion is needed on the far side. Ocean basins and moving water then modify this simple picture substantially.',
        ),
        section(
          'daily',
          'Why lunar timing shifts each day',
          'A lunar day is the time between successive passages of the Moon across a location’s meridian. During one rotation of Earth, the Moon moves forward in its orbit. A point on Earth must therefore rotate a little farther before it lines up with the Moon again.\n\nThe average lunar day is about 24 hours 50 minutes, longer than the 24-hour solar day. This is a moving-target effect, not a daily fifty-minute slowing of Earth’s spin. A dominant semidiurnal lunar tide has a period of roughly half that lunar day. Local basins and other tidal components prevent this simple period from being a complete timetable for every coast.',
        ),
        section(
          'monthly',
          'Spring tides and tidal range',
          'Tidal range is the height difference between high and low water. Near new and full moon, the Sun, Earth, and Moon are approximately aligned. The lunar and solar tidal effects reinforce, generally producing a larger range called a spring tide.\n\nThe comparison is between the high and low levels in a cycle, rather than a permanent increase in average sea level. Near quarter moon, the Sun’s and Moon’s directions are roughly at right angles; their combined effect generally produces a smaller range, called a neap tide. Spring tides occur through the year. Their name does not restrict them to the season of spring.',
        ),
        section(
          'basins',
          'Describing the daily pattern',
          'A semidiurnal tidal pattern has two high waters and two low waters in a lunar day. If successive high waters, and successive low waters, have noticeably different heights, the pattern is called mixed semidiurnal. A diurnal pattern instead has one high and one low in a day.\n\nThese terms describe the number and relative heights of the tides at a location. Spring and neap describe a different comparison: the changing high-to-low range over the lunar month. A place can therefore have a mixed semidiurnal daily pattern while also experiencing spring and neap variation. The two labels answer different questions.',
        ),
        section(
          'weather',
          'Astronomical tide and observed water level',
          'An astronomical tide prediction estimates the recurring water-level changes driven by the Moon and Sun. The observed level also responds to weather. Wind can pile water against a coast, and atmospheric pressure changes can alter the sea surface. Storm surge can consequently put the water above the astronomical prediction.\n\nThat difference does not by itself show that the lunar phase was wrong or that lunar gravity stopped acting. Several effects are present at once. Compare the predicted astronomical component with the measured level, then consider wind and pressure as additional causes. Their contribution is especially important during storms and in shallow coastal waters.',
        ),
      ],
    },
    questions: [
      {
        id: 'tides-1',
        prompt: 'What aspect of the Moon’s gravity is central to generating tides?',
        options: [
          {
            id: '0',
            text: 'Its pull is identical everywhere on Earth.',
          },
          {
            id: '1',
            text: 'Its pull varies across Earth.',
          },
          {
            id: '2',
            text: 'It repels water on the far side.',
          },
          {
            id: '3',
            text: 'It acts on oceans but not solid Earth.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation:
          'Tide generation depends on differences in gravitational acceleration across Earth.',
        sectionIds: ['difference'],
        evidence:
          'The article explains stronger pull on the near side and weaker pull on the far side relative to Earth as a whole.',
        sourceSection: {
          title: 'Principal lunar semi-diurnal constituent',
          url: 'https://en.wikipedia.org/wiki/Tide#Principal_lunar_semi-diurnal_constituent',
        },
      },
      {
        id: 'tides-2',
        prompt: 'Why is a lunar day longer than a solar day on average?',
        options: [
          {
            id: '0',
            text: 'Earth’s rotation slows by fifty minutes every day.',
          },
          {
            id: '1',
            text: 'The Sun and Moon reach alignment once each solar day.',
          },
          {
            id: '2',
            text: 'The Moon moves in its orbit, so Earth must turn farther to face it again.',
          },
          {
            id: '3',
            text: 'Two high tides must fit inside exactly twenty-four solar hours.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation: 'The Moon’s orbital motion changes the direction Earth must turn to face.',
        sectionIds: ['daily'],
        evidence:
          'Earth must turn farther to catch the Moon because the Moon moves in the same sense as Earth rotates.',
        sourceSection: {
          title: 'Example calculation',
          url: 'https://en.wikipedia.org/wiki/Tide#Example_calculation',
        },
      },
      {
        id: 'tides-3',
        prompt: 'Which description matches a spring tide?',
        options: [
          {
            id: '0',
            text: 'A generally larger tidal range near new or full moon',
          },
          {
            id: '1',
            text: 'A tide that occurs only in March',
          },
          {
            id: '2',
            text: 'A permanent increase in average ocean depth',
          },
          {
            id: '3',
            text: 'The complete cancellation of lunar and solar effects',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation:
          'Aligned lunar and solar tidal effects tend to increase the high-to-low range near new and full moon.',
        sectionIds: ['monthly'],
        evidence:
          'Alignment near new and full moon reinforces the tide-generating effects and increases the range.',
        sourceSection: {
          title: 'Range variation: springs and neaps',
          url: 'https://en.wikipedia.org/wiki/Tide#Range_variation:_springs_and_neaps',
        },
      },
      {
        id: 'tides-4',
        prompt: 'A coast has two noticeably unequal high tides in a lunar day. Which term fits?',
        options: [
          {
            id: '0',
            text: 'Diurnal',
          },
          {
            id: '1',
            text: 'Neap by definition',
          },
          {
            id: '2',
            text: 'Equal semidiurnal',
          },
          {
            id: '3',
            text: 'Mixed semidiurnal',
          },
        ],
        answer: {
          kind: 'choice',
          value: '3',
        },
        explanation: 'Mixed semidiurnal tides have two highs and lows with unequal heights.',
        sectionIds: ['basins'],
        evidence:
          'Two unequal high waters and two unequal low waters are identified as a mixed semi-diurnal tide.',
        sourceSection: {
          title: 'Tide Table',
          url: 'https://en.wikipedia.org/wiki/Tide#Tide_Table',
        },
      },
      {
        id: 'tides-5',
        prompt:
          'During a storm, the measured coastal water level is above the astronomical tide prediction. Which additional effect can explain the difference?',
        options: [
          {
            id: '0',
            text: 'The astronomical model must assume the wrong lunar phase.',
          },
          {
            id: '1',
            text: 'Wind and atmospheric effects can add to the predicted tide.',
          },
          {
            id: '2',
            text: 'Tidal range and average water level must have increased by the same amount.',
          },
          {
            id: '3',
            text: 'Spring tides replace astronomical forcing with wind forcing.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation:
          'Weather can alter water level on top of the astronomical tide, including through storm surge.',
        sectionIds: ['weather'],
        evidence:
          'The article states that actual time and height are affected by wind and atmospheric pressure.',
        sourceSection: {
          title: 'Tide Table',
          url: 'https://en.wikipedia.org/wiki/Tide#Tide_Table',
        },
      },
    ],
  },
];
